import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../contexts/SidebarContext';
import productionService from '../../services/productionService';
import ProductionMetricsPanel from './components/ProductionMetricsPanel';
import RealTimeStatusBoard from './components/RealTimeStatusBoard';
import AlertManagementPanel from './components/AlertManagementPanel';
import ProductionAnalytics from './components/ProductionAnalytics';
import QualityControlWorkflow from './components/QualityControlWorkflow';
import PerformanceMonitoring from './components/PerformanceMonitoring';

const ManufacturerProductionDashboard = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [activeView, setActiveView] = useState('overview');
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Production', path: '/production-tracking-dashboard', icon: 'Factory' },
    { label: 'Tableau de Bord Fabricant', path: '/manufacturer-production-dashboard', icon: 'BarChart3' }
  ];

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const batchData = await productionService?.getAll();
      setBatches(batchData);
      
      calculateMetrics(batchData);
      checkAlerts(batchData);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Erreur lors du chargement des données');
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (batchData) => {
    const now = new Date();
    
    const totalBatches = batchData?.length || 0;
    const activeBatches = batchData?.filter(b => b?.status === 'in_progress')?.length || 0;
    const completedToday = batchData?.filter(b => {
      const completionDate = new Date(b?.completionDate);
      return b?.status === 'completed' && completionDate?.toDateString() === now?.toDateString();
    })?.length || 0;
    
    const qualityCheckBatches = batchData?.filter(b => b?.status === 'quality_check')?.length || 0;
    const delayedBatches = batchData?.filter(b => b?.status === 'delayed')?.length || 0;
    
    const avgProgress = batchData?.length > 0
      ? Math.round(batchData?.reduce((sum, b) => sum + (b?.progress || 0), 0) / batchData?.length)
      : 0;
    
    const onTimeDelivery = batchData?.filter(b => {
      if (b?.status !== 'completed') return false;
      const deadline = new Date(b?.deadline);
      const completion = new Date(b?.completionDate);
      return completion <= deadline;
    })?.length || 0;
    
    const onTimeRate = totalBatches > 0 ? Math.round((onTimeDelivery / totalBatches) * 100) : 0;

    setMetrics({
      totalBatches,
      activeBatches,
      completedToday,
      qualityCheckBatches,
      delayedBatches,
      avgProgress,
      onTimeRate
    });
  };

  const checkAlerts = (batchData) => {
    const now = new Date();
    const newAlerts = [];

    batchData?.forEach(batch => {
      const startDate = new Date(batch?.startDate);
      const deadline = new Date(batch?.deadline);
      const hoursSinceStart = (now - startDate) / (1000 * 60 * 60);
      const daysUntilDeadline = (deadline - now) / (1000 * 60 * 60 * 24);

      // 24-hour urgent alert
      if (hoursSinceStart > 24 && batch?.status === 'pending') {
        newAlerts?.push({
          id: `urgent-24h-${batch?.id}`,
          type: 'error',
          priority: 'urgent',
          batchId: batch?.id,
          batchNumber: batch?.batchNumber,
          title: 'URGENT: Délai 24h dépassé',
          message: `Le lot ${batch?.batchNumber} n'a pas été traité depuis ${Math.floor(hoursSinceStart)}h`,
          timestamp: now,
          escalation: 'immediate'
        });
      }

      // 10-day delivery warning
      if (daysUntilDeadline <= 10 && batch?.status !== 'completed') {
        newAlerts?.push({
          id: `delivery-10d-${batch?.id}`,
          type: daysUntilDeadline <= 2 ? 'error' : 'warning',
          priority: daysUntilDeadline <= 2 ? 'high' : 'medium',
          batchId: batch?.id,
          batchNumber: batch?.batchNumber,
          title: 'Échéance de livraison proche',
          message: `Livraison prévue dans ${Math.ceil(daysUntilDeadline)} jours pour ${batch?.batchNumber}`,
          timestamp: now,
          escalation: daysUntilDeadline <= 2 ? 'high' : 'normal'
        });
      }
    });

    setAlerts(newAlerts);
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const views = [
    { id: 'overview', label: 'Vue d\'ensemble'  },
    { id: 'status', label: 'Statut Temps Réel' },
    { id: 'alerts', label: 'Gestion Alertes' },
    { id: 'analytics', label: 'Analytiques' },
    { id: 'quality', label: 'Contrôle Qualité' },
    { id: 'performance', label: 'Performance'}
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
           <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 py-6 max-w-7xl">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Tableau de Bord Fabricant</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Supervision production avec traçabilité QR et analytiques de performance
                </p>
              </div>
              <Button
                variant="primary"
                iconName="RefreshCw"
                onClick={fetchDashboardData}
                disabled={loading}
              >
                Actualiser
              </Button>
            </div>

            {/* View Tabs */}
            <div className="bg-white border border-gray-200 rounded-lg mb-6">
              <div className="flex overflow-x-auto">
                {views?.map(view => (
                  <button
                    key={view?.id}
                    onClick={() => setActiveView(view?.id)}
                    className={`flex items-center gap-2 px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                      activeView === view?.id
                        ? 'border-primary text-primary bg-primary/5' :'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span className="material-icons text-lg">{view?.icon}</span>
                    {view?.label}
                    {view?.badge > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-error text-white">
                        {view?.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* View Content */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="bg-error/10 border border-error/30 rounded-lg p-4 text-error">
                {error}
              </div>
            ) : (
              <>
                {activeView === 'overview' && (
                  <ProductionMetricsPanel metrics={metrics} batches={batches} />
                )}

                {activeView === 'status' && (
                  <RealTimeStatusBoard batches={batches} onRefresh={fetchDashboardData} />
                )}

                {activeView === 'alerts' && (
                  <AlertManagementPanel alerts={alerts} batches={batches} onRefresh={fetchDashboardData} />
                )}

                {activeView === 'analytics' && (
                  <ProductionAnalytics batches={batches} metrics={metrics} />
                )}

                {activeView === 'quality' && (
                  <QualityControlWorkflow batches={batches} onRefresh={fetchDashboardData} />
                )}

                {activeView === 'performance' && (
                  <PerformanceMonitoring batches={batches} metrics={metrics} />
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManufacturerProductionDashboard;